import { FC } from 'react';
import { AlertInterface } from '../../../interfaces/AlertInterface';
import { AlertsDrawerEmptyState } from './AlertsDrawerEmptyState';
import { AlertItem } from './AlertItem';
import FetchError from '../FetchError';
import { IconAlertTriangleOff } from '@tabler/icons-react';
import { LinkWrappedCard } from '../LinkWrappedCard';
import { ExpiredLink } from './ExpiredLink';
import { DrawerContent } from '../SharedDrawer';
import { themeColors } from '../../../styles/globalStyleVars';
import styled from 'styled-components';
import {
  alertsDrawerContentPadding,
  alertsDrawerScrollbarStyles,
} from './AlertsDrawerScrollStyles';

interface Props {
  showEmptyState: boolean;
  alertsError: boolean;
  alertsOutages: AlertInterface[];
  setAlertChildCaseOpen: (val: AlertInterface | undefined) => void;
  alertsDrawerOpen: boolean;
  refetchAlerts: () => void;
  alertsLoading: boolean;
  activeLocation: string;
  showExpiredAlerts: boolean;
  setShowExpiredAlerts: (val: boolean) => void;
}

const DIGITAL_DASHBOARD_CARD = {
  text: {
    body: 'Wondering about the status of Customer Digital services like ordering and payments? Check the Digital Performance Dashboard for real-time status information for the Chick-fil-A App, third-party delivery, and chick-fil-a.com.',
    title: 'Digital Performance Dashboard',
  },
  url: 'https://cfa-customer-digital.statuspage.io/',
};

const ScrollableDrawerContent = styled(DrawerContent)`
  flex: 1;
  min-height: 0;
  padding: ${alertsDrawerContentPadding};
  border-right: 12px solid transparent;
  ${alertsDrawerScrollbarStyles}
`;

export const AlertsDrawerActiveContent: FC<Props> = ({
  showEmptyState,
  alertsError,
  alertsOutages,
  setAlertChildCaseOpen,
  alertsDrawerOpen,
  refetchAlerts,
  alertsLoading,
  activeLocation,
  showExpiredAlerts,
  setShowExpiredAlerts,
}) => {
  const renderEmptyStateContent = () => {
    if (alertsError) {
      return (
        <div style={{ marginTop: '144px' }}>
          <FetchError
            btnClick={refetchAlerts}
            errorType='modal-fetch'
            page='alerts-drawer'
          />
        </div>
      );
    }

    if (alertsLoading) {
      return (
        <AlertsDrawerEmptyState
          heading='Working on that...'
          showLoadingSpinner
        />
      );
    }

    return (
      <>
        <AlertsDrawerEmptyState
          heading='There are no known alerts or outages.'
          icon={
            <IconAlertTriangleOff size={24} color={themeColors.colorGray_5} />
          }
        />
        <LinkWrappedCard {...DIGITAL_DASHBOARD_CARD} />
        <ExpiredLink setShowExpiredAlerts={setShowExpiredAlerts} />
      </>
    );
  };

  const renderAlertsList = () => (
    <div className='alert-item' id='alerts-list-wrapper'>
      {alertsOutages.map((alert: AlertInterface, index: number) => (
        <AlertItem
          alert={alert}
          setAlertChildCaseOpen={setAlertChildCaseOpen}
          alertsDrawerOpen={alertsDrawerOpen}
          activeLocation={activeLocation}
          showSummaryByDefault
          key={index}
        />
      ))}
      <LinkWrappedCard {...DIGITAL_DASHBOARD_CARD} />
      {!showExpiredAlerts && (
        <ExpiredLink setShowExpiredAlerts={setShowExpiredAlerts} />
      )}
    </div>
  );

  return (
    <ScrollableDrawerContent>
      {showEmptyState ? renderEmptyStateContent() : renderAlertsList()}
    </ScrollableDrawerContent>
  );
};
